// Frontend - revert to working commit 553584836d52c071ffb3e06ba70de3e9d2b46c8f
import React, { useState, useEffect, useCallback } from 'react';
import { Routes, Route, useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { signOut, User } from 'firebase/auth';
import { auth } from './config/firebase';
import Auth from './components/Auth';
import OAuthCallbackHandler from './components/OAuthCallbackHandler';
import ActivityTracker from './components/ActivityTracker';
import ChatWindow from './components/ChatWindow';
import GoogleConnect from './components/GoogleConnect';
import ChatHistory from './components/ChatHistory';
import SharedConversations from './components/SharedConversations';
import ShareViewer from './components/ShareViewer';
import SharedLinkViewer from './components/SharedLinkViewer';
import WorkflowSubmission from './components/WorkflowSubmission';
import ScheduledTasksPage from './components/ScheduledTasks/ScheduledTasksPage';
import AdminDashboard from './components/Admin/AdminDashboard';
import CgBoostDashboard from './components/CgBoost/CgBoostDashboard';
import FinancialsDashboard from './components/Financials/FinancialsDashboard';
import ChannelIntelligenceDashboard from './components/ChannelIntelligence/ChannelIntelligenceDashboard';
import SalesDashboard from './components/Sales/SalesDashboard';
import AdOpsDashboard from './components/AdOps/AdOpsDashboard';
import InvoicingDashboard from './components/Invoicing/InvoicingDashboard';
import ReconciliationDashboard from './components/Reconciliation/ReconciliationDashboard';
import TakeoversDashboard from './components/Takeovers/TakeoversDashboard';
import TakeoversRoute from './components/Takeovers/TakeoversRoute';
import CreatorsDashboard from './components/Creators/CreatorsDashboard';
import DashboardLauncher from './components/Home/DashboardLauncher';
import SidebarTabs, { SidebarTab } from './components/SidebarTabs';
import NotificationContainer from './components/NotificationContainer';
import { useNotification } from './hooks/useNotification';
import { useInactivityTimeout } from './hooks/useInactivityTimeout';
import { usePermissions, PermissionKey } from './hooks/usePermissions';
import { useViewport } from './hooks/useViewport';
import { isAllowedDomain, orgFromEmail, UserOrganization } from './utils/authDomain';
import { invalidateGoogleConnectionStatus } from './services/api';
import './App.css';

// Safety net for a fully broken persistence layer. Under normal conditions
// onAuthStateChanged fires well within this window — most reloads in <500ms,
// slow networks/cold caches up to a few seconds. 10s is long enough that
// hitting it indicates a real failure, not a normal cold load.
const AUTH_RESOLVE_TIMEOUT_MS = 10_000;

function App() {
  const [user, setUser] = useState<User | null>(() => auth.currentUser);
  const [userOrganization, setUserOrganization] = useState<UserOrganization>(() =>
    orgFromEmail(auth.currentUser?.email),
  );
  const [authResolving, setAuthResolving] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    if (auth.currentUser) return false;
    // Always treat the pre-listener window as "resolving" so Auth renders the
    // loading splash instead of the login card. This matters during the OAuth
    // handoff (?oauth_token=…): OAuthCallbackHandler covers the screen while
    // it works, but there's a race between it dismissing its own splash and
    // App's onAuthStateChanged firing with the new user. If authResolving were
    // false during that gap, Auth would flash the login card.
    return true;
  });

  const [currentSessionId, setCurrentSessionId] = useState<string | null>(null);
  const [selectedSessionId, setSelectedSessionId] = useState<string | null>(null);
  const [selectedShareId, setSelectedShareId] = useState<string | null>(null);
  const [sidebarVisible, setSidebarVisible] = useState(false);
  const [helpModalVisible, setHelpModalVisible] = useState(false);
  const [activeTab, setActiveTab] = useState<SidebarTab>('history');
  const [unreadSharesCount, setUnreadSharesCount] = useState(0);
  const [viewingShare, setViewingShare] = useState(false);
  const [sharesRefreshTrigger, setSharesRefreshTrigger] = useState(0);
  const [newChatTrigger, setNewChatTrigger] = useState(0);

  const { notifications, showSuccess, showError, showWarning, closeNotification, clearAllNotifications } = useNotification();

  const handleInactivityWarning = useCallback(() => {
    showWarning({
      title: 'Inactivity Warning',
      message: 'You will be signed out in 15 minutes due to inactivity. Move your mouse or press a key to stay signed in.',
      duration: 60000,
    });
  }, [showWarning]);

  const handleInactivityTimeout = useCallback(() => {
    console.log('[App] User signed out due to inactivity (8 hours)');
  }, []);

  const { getLastActivity } = useInactivityTimeout({
    enabled: !!user,
    timeoutMinutes: 480,
    warningMinutes: 15,
    onWarning: handleInactivityWarning,
    onTimeout: handleInactivityTimeout,
  });

  useEffect(() => {
    let resolved = false;
    const finishResolving = () => {
      if (!resolved) {
        resolved = true;
        setAuthResolving(false);
      }
    };

    const fallbackTimer = setTimeout(() => {
      if (!resolved) {
        console.warn('[App] Auth rehydration exceeded 10s — falling back to sign-in screen');
      }
      finishResolving();
    }, AUTH_RESOLVE_TIMEOUT_MS);

    const unsubscribe = auth.onAuthStateChanged((currentUser) => {
      finishResolving();

      if (currentUser && currentUser.email && !isAllowedDomain(currentUser.email)) {
        console.warn('[App] Disallowed email signed in, signing out:', currentUser.email);
        showError({
          title: 'Sign-in not allowed',
          message:
            'Only Culture Genesis and Culture Genesis Partnerships email addresses are allowed.',
          duration: 8000,
        });
        invalidateGoogleConnectionStatus();
        signOut(auth).catch((err) => console.error('[App] Sign-out failed:', err));
        return;
      }

      setUser(currentUser);
      setUserOrganization(orgFromEmail(currentUser?.email));

      if (!currentUser) {
        invalidateGoogleConnectionStatus();
        clearAllNotifications();
      }
    });

    return () => {
      clearTimeout(fallbackTimer);
      unsubscribe();
    };
  }, [clearAllNotifications, showError]);

  const handleSelectSession = useCallback((sessionId: string) => {
    setSelectedSessionId(sessionId);
    setViewingShare(false);
    setSelectedShareId(null);
    if (activeTab === 'shared') {
      setActiveTab('history');
    }
  }, [activeTab]);

  const handleNewChat = () => {
    setSelectedSessionId(null);
    setCurrentSessionId(null);
    setViewingShare(false);
    setSelectedShareId(null);
    setNewChatTrigger(prev => prev + 1);
    if (activeTab === 'shared') {
      setActiveTab('history');
    }
  };

  const handleSelectShare = (shareId: string) => {
    setSelectedShareId(shareId);
    setViewingShare(true);
  };

  const handleCloseShareViewer = () => {
    setViewingShare(false);
    setSelectedShareId(null);
  };

  const handleTabChange = (tab: SidebarTab) => {
    setActiveTab(tab);
  };

  const handleUnreadCountChange = (count: number) => {
    setUnreadSharesCount(count);
  };

  const handleShareMarkedAsViewed = () => {
    console.log('[App] Share marked as viewed, triggering refresh');
    setSharesRefreshTrigger(prev => prev + 1);
  };

  const oauthErrorHandler = useCallback(
    (message: string) => {
      showError({
        title: 'Sign-in failed',
        message,
        duration: 8000,
      });
    },
    [showError],
  );

  return (
    <>
      {/* Mounted exactly once. Renders its own splash when consuming the
          ?oauth_token=… redirect, otherwise renders nothing. */}
      <OAuthCallbackHandler onError={oauthErrorHandler} />
      {/* Dashboard opens + activity pings for Admin → Login Activity. Renders nothing. */}
      <ActivityTracker user={user} getLastActivity={getLastActivity} />

      <Routes>
        {/* Share Link Route - Public (requires auth but no sidebar) */}
        <Route path="/share/:shareLinkId" element={<ShareLinkRoute user={user} authResolving={authResolving} />} />

        {/* Workflow Submission Route */}
        <Route path="/workflow" element={<WorkflowRoute
          user={user}
          authResolving={authResolving}
          showSuccess={showSuccess}
          showError={showError}
        />} />

        {/* Admin Route — gated by admin.view permission */}
        <Route path="/admin/*" element={
          user ? (
            <PermissionGate
              permission="admin.view"
              deniedMessage="Access denied. Admin privileges required."
            >
              <div className="App">
                <Auth user={user} userOrganization={userOrganization} />
                <AdminDashboard />
              </div>
            </PermissionGate>
          ) : (
            <Auth user={user} authResolving={authResolving} />
          )
        } />

        {/* CG Boost Dashboard Route — gated by cgBoost.view permission */}
        <Route path="/dashboards/cg-boost/*" element={
          user ? (
            <PermissionGate
              permission="cgBoost.view"
              deniedMessage="Access denied. CG Boost dashboard requires the cgBoost.view permission."
            >
              <div className="App">
                <Auth user={user} userOrganization={userOrganization} />
                <CgBoostDashboard />
              </div>
            </PermissionGate>
          ) : (
            <Auth user={user} authResolving={authResolving} />
          )
        } />

        {/* Financials Dashboard Route — gated by financials.view permission */}
        <Route path="/dashboards/financials/*" element={
          user ? (
            <PermissionGate
              permission="financials.view"
              deniedMessage="Access denied. Financials dashboard requires the financials.view permission."
            >
              <div className="App">
                <Auth user={user} userOrganization={userOrganization} />
                <FinancialsDashboard />
              </div>
            </PermissionGate>
          ) : (
            <Auth user={user} authResolving={authResolving} />
          )
        } />

        {/* Sales Dashboard Route — gated by sales.view permission */}
        <Route path="/dashboards/sales/*" element={
          user ? (
            <PermissionGate
              permission="sales.view"
              deniedMessage="Access denied. Sales dashboard requires the sales.view permission."
            >
              <div className="App">
                <Auth user={user} userOrganization={userOrganization} />
                <SalesDashboard />
              </div>
            </PermissionGate>
          ) : (
            <Auth user={user} authResolving={authResolving} />
          )
        } />

        {/* Channel Intelligence Dashboard Route — gated by channelIntelligence.view permission */}
        <Route path="/dashboards/channel-intelligence/*" element={
          user ? (
            <PermissionGate
              permission="channelIntelligence.view"
              deniedMessage="Access denied. Channel Intelligence requires the channelIntelligence.view permission."
            >
              <div className="App">
                <Auth user={user} userOrganization={userOrganization} />
                <ChannelIntelligenceDashboard userOrganization={userOrganization} />
              </div>
            </PermissionGate>
          ) : (
            <Auth user={user} authResolving={authResolving} />
          )
        } />

        {/* Ad Ops Dashboard Route — gated by adOps.view permission */}
        <Route path="/dashboards/ad-ops/*" element={
          user ? (
            <PermissionGate
              permission="adOps.view"
              deniedMessage="Access denied. Ad Ops dashboard requires the adOps.view permission."
            >
              <div className="App">
                <Auth user={user} userOrganization={userOrganization} />
                <AdOpsDashboard />
              </div>
            </PermissionGate>
          ) : (
            <Auth user={user} authResolving={authResolving} />
          )
        } />

        {/* Invoicing Tracker Route — gated by invoicing.view permission */}
        <Route path="/dashboards/invoicing/*" element={
          user ? (
            <PermissionGate
              permission="invoicing.view"
              deniedMessage="Access denied. Invoicing Tracker requires the invoicing.view permission."
            >
              <div className="App">
                <Auth user={user} userOrganization={userOrganization} />
                <InvoicingDashboard />
              </div>
            </PermissionGate>
          ) : (
            <Auth user={user} authResolving={authResolving} />
          )
        } />

        {/* Reconciliation Route — gated by invoicing.view permission */}
        <Route path="/dashboards/reconciliation/*" element={
          user ? (
            <PermissionGate
              permission="invoicing.view"
              deniedMessage="Access denied. Reconciliation requires the invoicing.view permission."
            >
              <div className="App">
                <Auth user={user} userOrganization={userOrganization} />
                <ReconciliationDashboard />
              </div>
            </PermissionGate>
          ) : (
            <Auth user={user} authResolving={authResolving} />
          )
        } />

        {/* Takeovers Route — gated by takeovers.view permission; old Creator Pages links go to Creators */}
        <Route path="/dashboards/takeovers/*" element={
          <TakeoversRoute>
            {user ? (
              <PermissionGate
                permission="takeovers.view"
                deniedMessage="Access denied. Takeovers requires the takeovers.view permission."
              >
                <div className="App">
                  <Auth user={user} userOrganization={userOrganization} />
                  <TakeoversDashboard />
                </div>
              </PermissionGate>
            ) : (
              <Auth user={user} authResolving={authResolving} />
            )}
          </TakeoversRoute>
        } />

        {/* Creators Route — creator/agency report dashboards, gated by clientReports.manage */}
        <Route path="/dashboards/creators/*" element={
          user ? (
            <PermissionGate
              permission="clientReports.manage"
              deniedMessage="Access denied. Creators requires the Creators Dashboard permission."
            >
              <div className="App">
                <Auth user={user} userOrganization={userOrganization} />
                <CreatorsDashboard />
              </div>
            </PermissionGate>
          ) : (
            <Auth user={user} authResolving={authResolving} />
          )
        } />

        {/* Dashboard Launcher (post-login home, CG Boost spec §3) */}
        <Route path="/" element={<DashboardLauncher
          user={user}
          authResolving={authResolving}
          userOrganization={userOrganization}
          showSuccess={showSuccess}
          showError={showError}
          showWarning={showWarning}
        />} />

        {/* Chat (formerly mounted at "/") */}
        <Route path="/chat/*" element={<MainAppRoute
          user={user}
          authResolving={authResolving}
          userOrganization={userOrganization}
          currentSessionId={currentSessionId}
          selectedSessionId={selectedSessionId}
          selectedShareId={selectedShareId}
          sidebarVisible={sidebarVisible}
          setSidebarVisible={setSidebarVisible}
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          unreadSharesCount={unreadSharesCount}
          viewingShare={viewingShare}
          sharesRefreshTrigger={sharesRefreshTrigger}
          newChatTrigger={newChatTrigger}
          handleSelectSession={handleSelectSession}
          handleNewChat={handleNewChat}
          handleSelectShare={handleSelectShare}
          handleCloseShareViewer={handleCloseShareViewer}
          handleTabChange={handleTabChange}
          handleUnreadCountChange={handleUnreadCountChange}
          handleShareMarkedAsViewed={handleShareMarkedAsViewed}
          setCurrentSessionId={setCurrentSessionId}
          notifications={notifications}
          closeNotification={closeNotification}
          showSuccess={showSuccess}
          showError={showError}
          showWarning={showWarning}
          helpModalVisible={helpModalVisible}
          setHelpModalVisible={setHelpModalVisible}
        />} />
      </Routes>
    </>
  );
}

// Permission-aware route gate. Renders children only if the current user has
// the required permission key; otherwise shows a denial message. While the
// permission map is loading, shows a small loading indicator instead of
// flashing "access denied" on already-authorized users.
interface PermissionGateProps {
  permission: PermissionKey;
  deniedMessage: string;
  children: React.ReactNode;
}

const PermissionGate: React.FC<PermissionGateProps> = ({ permission, deniedMessage, children }) => {
  const { loading, has, error, refresh } = usePermissions();
  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh' }}>
        <p>Checking permissions…</p>
      </div>
    );
  }
  if (!has(permission)) {
    // A failed permissions fetch is not a missing grant: say so and offer a retry
    // instead of the denial message (which would send the user to an admin who
    // sees the grant already in place).
    if (error) {
      return (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100vh', gap: 12 }}>
          <p>Couldn't load your permissions ({error}).</p>
          <button type="button" onClick={() => { void refresh(); }} style={{ padding: '8px 16px', cursor: 'pointer' }}>
            Retry
          </button>
        </div>
      );
    }
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh' }}>
        <p>{deniedMessage}</p>
      </div>
    );
  }
  return <>{children}</>;
};

// Share Link Route Component
interface ShareLinkRouteProps {
  user: User | null;
  authResolving: boolean;
}

const ShareLinkRoute: React.FC<ShareLinkRouteProps> = ({ user, authResolving }) => {
  const { shareLinkId } = useParams<{ shareLinkId: string }>();
  const navigate = useNavigate();

  const handleNavigateToSession = (sessionId: string) => {
    navigate(`/chat?session=${sessionId}`);
  };

  if (!user) {
    return <Auth user={user} authResolving={authResolving} />;
  }

  if (!shareLinkId || shareLinkId.trim().length === 0) {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100vh',
        gap: '1rem',
        padding: '2rem',
        textAlign: 'center',
      }}>
        <div style={{
          fontSize: '1rem',
          color: 'var(--danger)',
        }}>
          Invalid share link URL
        </div>
        <button
          onClick={() => navigate('/')}
          style={{
            padding: '0.5rem 1rem',
            borderRadius: 'var(--radius-md)',
            fontSize: '0.875rem',
            fontWeight: 500,
            backgroundColor: 'var(--accent)',
            color: 'white',
            border: 'none',
            cursor: 'pointer',
          }}
        >
          Go to Home
        </button>
      </div>
    );
  }

  return <SharedLinkViewer shareLinkId={shareLinkId} onNavigateToSession={handleNavigateToSession} />;
};

// Workflow Route Component
interface WorkflowRouteProps {
  user: User | null;
  authResolving: boolean;
  showSuccess: (notification: any) => void;
  showError: (notification: any) => void;
}

const WorkflowRoute: React.FC<WorkflowRouteProps> = ({
  user,
  authResolving,
  showSuccess,
  showError,
}) => {
  const navigate = useNavigate();

  if (!user) {
    return <Auth user={user} authResolving={authResolving} />;
  }

  const handleSuccess = (driveUrl: string) => {
    showSuccess({
      title: 'Workflow Submitted!',
      message: 'Your workflow request has been saved to Google Drive.',
      duration: 5000,
    });
  };

  const handleError = (error: string) => {
    showError({
      title: 'Submission Failed',
      message: error,
      duration: 6000,
    });
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      overflow: 'auto',
      backgroundColor: 'var(--bg-surface-1)',
    }}>
      {/* Back to Chat Button */}
      <button
        onClick={() => navigate('/chat')}
        style={{
          position: 'fixed',
          top: '1rem',
          left: '1rem',
          padding: '0.5rem 1rem',
          borderRadius: '0.5rem',
          fontSize: '0.875rem',
          fontWeight: 500,
          backgroundColor: 'var(--accent)',
          color: 'white',
          border: 'none',
          cursor: 'pointer',
          zIndex: 10,
          boxShadow: 'var(--shadow-sm)',
        }}
      >
        ← Back to Chat
      </button>

      <WorkflowSubmission
        user={user}
        onSuccess={handleSuccess}
        onError={handleError}
      />
    </div>
  );
};

// Main App Route Component
interface MainAppRouteProps {
  user: User | null;
  authResolving: boolean;
  userOrganization: UserOrganization;
  currentSessionId: string | null;
  selectedSessionId: string | null;
  selectedShareId: string | null;
  sidebarVisible: boolean;
  setSidebarVisible: (visible: boolean) => void;
  activeTab: SidebarTab;
  setActiveTab: (tab: SidebarTab) => void;
  unreadSharesCount: number;
  viewingShare: boolean;
  sharesRefreshTrigger: number;
  newChatTrigger: number;
  handleSelectSession: (sessionId: string) => void;
  handleNewChat: () => void;
  handleSelectShare: (shareId: string) => void;
  handleCloseShareViewer: () => void;
  handleTabChange: (tab: SidebarTab) => void;
  handleUnreadCountChange: (count: number) => void;
  handleShareMarkedAsViewed: () => void;
  setCurrentSessionId: (sessionId: string | null) => void;
  notifications: any[];
  closeNotification: (id: string) => void;
  showSuccess: (notification: any) => void;
  showError: (notification: any) => void;
  showWarning: (notification: any) => void;
  helpModalVisible: boolean;
  setHelpModalVisible: (visible: boolean) => void;
}

const MainAppRoute: React.FC<MainAppRouteProps> = ({
  user,
  authResolving,
  userOrganization,
  currentSessionId,
  selectedSessionId,
  selectedShareId,
  sidebarVisible,
  setSidebarVisible,
  activeTab,
  unreadSharesCount,
  viewingShare,
  sharesRefreshTrigger,
  newChatTrigger,
  handleSelectSession,
  handleNewChat,
  handleSelectShare,
  handleCloseShareViewer,
  handleTabChange,
  handleUnreadCountChange,
  handleShareMarkedAsViewed,
  setCurrentSessionId,
  notifications,
  closeNotification,
  showSuccess,
  showError,
  showWarning,
  helpModalVisible,
  setHelpModalVisible,
}) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { isMobile } = useViewport();

  useEffect(() => {
    const sessionParam = searchParams.get('session');
    if (sessionParam && user) {
      console.log('[MainAppRoute] Auto-loading session from query parameter:', sessionParam);
      handleSelectSession(sessionParam);
      setSearchParams({});
    }
  }, [searchParams, user, handleSelectSession, setSearchParams]);

  if (!user) {
    return <Auth user={user} authResolving={authResolving} />;
  }

  const sidebarStyle: React.CSSProperties = isMobile
    ? {
        ...styles.sidebar,
        position: 'fixed' as const,
        top: 'var(--header-height)', // Clear the in-flow header (single row on mobile)
        bottom: 0,
        left: 0,
        width: sidebarVisible ? 'min(86vw, 360px)' : '0px',
        zIndex: 'var(--z-flyout)' as React.CSSProperties['zIndex'],
        boxShadow: sidebarVisible ? 'var(--shadow-lg)' : 'none',
        // Clear the iPhone home indicator (viewport-fit=cover)
        paddingBottom: 'calc(var(--space-lg) + env(safe-area-inset-bottom))',
        ...(sidebarVisible ? {} : { borderRight: 'none' }),
      }
    : {
        ...styles.sidebar,
        width: sidebarVisible ? '420px' : '0px',
        ...(sidebarVisible ? {} : { borderRight: 'none' }),
      };

  return (
    <div className="App">
      <Auth
        user={user}
        userOrganization={userOrganization}
        sidebarVisible={sidebarVisible}
        onToggleSidebar={() => setSidebarVisible(!sidebarVisible)}
        onShowHelp={() => setHelpModalVisible(true)}
        googleConnectComponent={
          <GoogleConnect
            user={user}
            userOrganization={userOrganization}
            onSuccess={showSuccess}
            onError={showError}
            onWarning={showWarning}
          />
        }
      />

      {/* Notification Container */}
      <NotificationContainer
        notifications={notifications}
        onClose={closeNotification}
      />

      <div style={styles.container}>
        {/* Mobile backdrop — taps close the overlay sidebar */}
        {isMobile && sidebarVisible && (
          <div
            style={styles.mobileBackdrop}
            onClick={() => setSidebarVisible(false)}
            aria-hidden="true"
          />
        )}

        {/* Sidebar — pushes content on desktop, overlays content on mobile */}
        <div style={sidebarStyle}>
          {sidebarVisible && (
            <>
              {/* Tab Switcher */}
              <SidebarTabs
                activeTab={activeTab}
                onTabChange={handleTabChange}
                unreadCount={unreadSharesCount}
              />

              {/* Chat History Tab */}
              {activeTab === 'history' && (
                <ChatHistory
                  onSelectSession={handleSelectSession}
                  currentSessionId={currentSessionId}
                  onNewChat={handleNewChat}
                />
              )}

              {/* Shared Conversations Tab */}
              {activeTab === 'shared' && (
                <SharedConversations
                  onSelectShare={handleSelectShare}
                  currentShareId={selectedShareId}
                  onUnreadCountChange={handleUnreadCountChange}
                  onError={(message) => showError({ title: 'Error', message })}
                  enablePolling={sidebarVisible && activeTab === 'shared'}
                  pollingInterval={30000}
                  refreshTrigger={sharesRefreshTrigger}
                />
              )}

              {/* Scheduled Tasks Tab */}
              {activeTab === 'scheduled' && (
                <ScheduledTasksPage
                  onClose={() => handleTabChange('history')}
                  showSuccess={showSuccess}
                  showError={showError}
                />
              )}
            </>
          )}
        </div>

        {/* Main content area */}
        <div style={styles.main}>
          {viewingShare && selectedShareId ? (
            <ShareViewer
              shareId={selectedShareId}
              onClose={handleCloseShareViewer}
              onMarkedAsViewed={handleShareMarkedAsViewed}
            />
          ) : (
            <ChatWindow
              selectedSessionId={selectedSessionId}
              userOrganization={userOrganization}
              newChatTrigger={newChatTrigger}
              onSessionChange={setCurrentSessionId}
              onShareSuccess={(recipientName) => {
                showSuccess({
                  title: 'Conversation Shared!',
                  message: `Successfully shared with ${recipientName}`,
                  duration: 4000
                });
              }}
            />
          )}
        </div>

        {/* Help Button — hidden on mobile, where it collides with the header
            drawer and ChatWindow's Reset button; the drawer has a Search tips
            entry instead */}
        {!isMobile && (
          <button
            onClick={() => setHelpModalVisible(true)}
            style={styles.helpButton}
            title="Search tips and best practices"
          >
            ?
          </button>
        )}

        {/* Help Modal */}
        {helpModalVisible && (
          <div style={styles.modalOverlay} onClick={() => setHelpModalVisible(false)}>
            <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
              <div style={styles.modalHeader}>
                <h2 style={styles.modalTitle}>Search Tips & Best Practices</h2>
                <button
                  onClick={() => setHelpModalVisible(false)}
                  style={styles.modalCloseButton}
                >
                  ✕
                </button>
              </div>
              <div style={styles.modalBody}>
                <section style={styles.helpSection}>
                  <h3 style={styles.helpSectionTitle}>🚀 Getting Started</h3>
                  <ul style={styles.helpList}>
                    <li style={styles.helpListItem}>
                      <strong>Natural language queries:</strong> Ask questions as you would to a colleague - "Find all female creators with 1M+ Instagram followers"
                    </li>
                    <li style={styles.helpListItem}>
                      <strong>Be specific:</strong> Include details like names, dates, numbers, and locations for better results
                    </li>
                    <li style={styles.helpListItem}>
                      <strong>Follow-up questions:</strong> Culture Genius remembers context - refine results with "Show me just the top 10" or "Exclude anyone under 500k followers"
                    </li>
                  </ul>
                </section>

                <section style={styles.helpSection}>
                  <h3 style={styles.helpSectionTitle}>📎 File Attachments (NEW!)</h3>
                  <ul style={styles.helpList}>
                    <li style={styles.helpListItem}>
                      <strong>Upload documents:</strong> Click the 📎 button or drag files into the chat to provide context
                    </li>
                    <li style={styles.helpListItem}>
                      <strong>Supported formats:</strong> PDF, XLSX, CSV, TXT (10MB per file, 25MB total)
                    </li>
                    <li style={styles.helpListItem}>
                      <strong>Ask questions about files:</strong> "Summarize this report", "Extract key metrics from this spreadsheet", "Find creators mentioned in this document"
                    </li>
                    <li style={styles.helpListItem}>
                      <strong>Combine with search:</strong> Upload a creator list CSV and ask "Find social media profiles for everyone in this file"
                    </li>
                  </ul>
                </section>

                <section style={styles.helpSection}>
                  <h3 style={styles.helpSectionTitle}>🗂️ Airtable Creator Database</h3>
                  <ul style={styles.helpList}>
                    <li style={styles.helpListItem}>
                      <strong>Demographic filters:</strong> "Find Asian/Pacific Islander creators" or "Show me female creators aged 25-34"
                    </li>
                    <li style={styles.helpListItem}>
                      <strong>Follower counts (text ranges):</strong> Use "1m+", "500k+", "100k-500k" - the database stores ranges, not exact numbers
                    </li>
                    <li style={styles.helpListItem}>
                      <strong>Interest matching:</strong> "Find creators interested in Travel and Food" or "Show Beauty creators"
                    </li>
                    <li style={styles.helpListItem}>
                      <strong>Get just names:</strong> Say "just show names" or "list names only" for concise results without full profile data
                    </li>
                  </ul>
                </section>

                <section style={styles.helpSection}>
                  <h3 style={styles.helpSectionTitle}>🔍 Google Drive & Gmail</h3>
                  <ul style={styles.helpList}>
                    <li style={styles.helpListItem}>
                      <strong>Drive searches:</strong> "Find the Monthly Tracker for January 2025" or "Search for campaign documents from last month"
                    </li>
                    <li style={styles.helpListItem}>
                      <strong>Folder navigation:</strong> If asked to pick folders, select from the UI instead of typing - it's faster and more accurate
                    </li>
                    <li style={styles.helpListItem}>
                      <strong>Gmail searches:</strong> "Find emails from john@company.com about Q4 revenue" or "Show me messages with 'partnership' in the subject from last week"
                    </li>
                    <li style={styles.helpListItem}>
                      <strong>Content analysis:</strong> "Summarize the latest email from the CEO" or "Extract action items from recent campaign emails"
                    </li>
                  </ul>
                </section>

                <section style={styles.helpSection}>
                  <h3 style={styles.helpSectionTitle}>📈 Google Ad Manager</h3>
                  <ul style={styles.helpList}>
                    <li style={styles.helpListItem}>
                      <strong>Campaign searches:</strong> "Show me all active Ad Manager campaigns" or "Find orders for Nike"
                    </li>
                    <li style={styles.helpListItem}>
                      <strong>Status filtering:</strong> Search by order status - APPROVED, DRAFT, PAUSED, CANCELED, PENDING_APPROVAL
                    </li>
                    <li style={styles.helpListItem}>
                      <strong>Advertiser lookup:</strong> "List all advertisers in Ad Manager" or "Find agencies we work with"
                    </li>
                    <li style={styles.helpListItem}>
                      <strong>Ad inventory:</strong> Query ad units and placement information - "Show me Video ad units"
                    </li>
                    <li style={styles.helpListItem}>
                      <strong>Date ranges:</strong> Find campaigns by date - "Show orders updated after January 1, 2025"
                    </li>
                    <li style={styles.helpListItem}>
                      <strong>Combined filters:</strong> "Find approved campaigns for Q1 2025" combines status and name filtering
                    </li>
                  </ul>
                </section>

                <section style={styles.helpSection}>
                  <h3 style={styles.helpSectionTitle}>⚡ Performance & Efficiency</h3>
                  <ul style={styles.helpList}>
                    <li style={styles.helpListItem}>
                      <strong>One task at a time:</strong> Complete one query before asking the next - this ensures better context and results
                    </li>
                    <li style={styles.helpListItem}>
                      <strong>Be patient with complex tasks:</strong> Searches involving multiple sources (Drive + Airtable + Gmail) may take 30-60 seconds
                    </li>
                    <li style={styles.helpListItem}>
                      <strong>Request specific formats:</strong> "Create a PDF report" or "Export as spreadsheet" for deliverable outputs
                    </li>
                    <li style={styles.helpListItem}>
                      <strong>Use Reset wisely:</strong> Clear chat when switching to a completely new topic to avoid confusion from previous context
                    </li>
                  </ul>
                </section>

                <section style={styles.helpSection}>
                  <h3 style={styles.helpSectionTitle}>📚 Example Queries</h3>
                  <ul style={styles.helpList}>
                    <li style={styles.helpListItem}>
                      <strong>📁 Drive:</strong> "Show me all files in the Monthly Tracker folder" • "How much did [Creator Name] earn in September 2025?"
                    </li>
                    <li style={styles.helpListItem}>
                      <strong>📊 Airtable:</strong> "Show me all creator names with over 1M Instagram followers" • "Find all Asian creators with 500K+ Instagram followers interested in Food or Travel"
                    </li>
                    <li style={styles.helpListItem}>
                      <strong>📧 Gmail:</strong> "Show me emails from advertisers in the last week" • "Find all emails from Nike in 2025"
                    </li>
                    <li style={styles.helpListItem}>
                      <strong>📈 Ad Manager:</strong> "List all active Ad Manager campaigns" • "Find orders for Nike updated in the last month" • "Show me all advertisers" • "What ad units do we have?"
                    </li>
                    <li style={styles.helpListItem}>
                      <strong>📄 Create:</strong> "Create a PDF report of all female creators with 1M+ Instagram followers" • "Create a Google Doc with a list of all Beauty creators"
                    </li>
                    <li style={styles.helpListItem}>
                      <strong>🔗 Combined:</strong> "Read the Monthly Tracker for [Creator Name] and create a summary report" • "Find creators in Airtable and check their recent emails" • "Show me Ad Manager campaigns and match them with creator earnings"
                    </li>
                  </ul>
                </section>

                <section style={styles.helpSection}>
                  <h3 style={styles.helpSectionTitle}>💡 Pro Tips</h3>
                  <ul style={styles.helpList}>
                    <li style={styles.helpListItem}>
                      <strong>Leverage memory:</strong> Culture Genius remembers your conversation - refer to previous results with "from that list" or "those creators"
                    </li>
                    <li style={styles.helpListItem}>
                      <strong>Ask for summaries:</strong> When results are large, say "summarize this" or "give me the top 5 highlights"
                    </li>
                    <li style={styles.helpListItem}>
                      <strong>Combine data sources:</strong> "Find creators in Airtable and check if they're mentioned in my Gmail from last month" or "Match Ad Manager advertisers with creator earnings from Drive"
                    </li>
                    <li style={styles.helpListItem}>
                      <strong>Iterate and refine:</strong> Start broad, then narrow down - "Show all Beauty creators" → "From that list, only show 1M+ followers"
                    </li>
                    <li style={styles.helpListItem}>
                      <strong>Ad Manager insights:</strong> Cross-reference campaign data with creator performance - "Which creators had Nike campaigns in Q1?" then "Show me their earnings for that period"
                    </li>
                  </ul>
                </section>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

const styles = {
  container: {
    display: 'flex',
    flex: 1, // Take remaining space
    position: 'relative' as const,
    overflow: 'hidden', // Prevent scrolling on the container
    minHeight: 0, // Important for flex children
  },
  sidebar: {
    borderRight: '1px solid rgba(229, 231, 235, 0.3)',
    backgroundColor: 'rgba(255, 255, 255, 0.85)',
    backdropFilter: 'blur(20px)',
    overflowY: 'auto' as const,
    overflowX: 'hidden' as const,
    display: 'flex',
    flexDirection: 'column' as const,
    transition: 'width 0.3s ease-in-out',
    flexShrink: 0,
    padding: 'var(--space-lg) 0',
  },
  mobileBackdrop: {
    position: 'fixed' as const,
    inset: 0,
    background: 'var(--bg-overlay)',
    zIndex: 'calc(var(--z-flyout) - 1)',
  },
  main: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column' as const,
    minWidth: 0,
    minHeight: 0, // Let ChatWindow's height:100% resolve instead of growing past the viewport
    width: '100%',
    position: 'relative' as const,
  },
  helpButton: {
    position: 'fixed' as const,
    right: '12px',
    top: '80px', // Position below header to match sidebar toggle
    width: '40px',
    height: '40px',
    borderRadius: '50%',
    border: '1px solid var(--border-1)',
    backgroundColor: 'var(--bg-surface-1)',
    color: 'var(--fg-1)',
    fontSize: '1.2rem',
    fontWeight: 600,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: 'var(--shadow-lg)',
    transition: 'all 0.3s ease',
    zIndex: 'var(--z-overlay)',
    fontFamily: 'var(--font-sans)',
  } as React.CSSProperties,
  modalOverlay: {
    position: 'fixed' as const,
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 'var(--z-modal)',
    animation: 'fadeIn 0.2s ease-out',
  },
  modalContent: {
    backgroundColor: 'var(--bg-surface-1)',
    borderRadius: '16px',
    maxWidth: '700px',
    width: '90%',
    maxHeight: '80vh',
    display: 'flex',
    flexDirection: 'column' as const,
    boxShadow: 'var(--shadow-xl)',
    animation: 'scaleIn 0.3s ease-out',
  },
  modalHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: 'var(--space-lg)',
    borderBottom: '1px solid var(--border-1)',
  },
  modalTitle: {
    margin: 0,
    fontSize: '1.25rem',
    fontWeight: 600,
    color: 'var(--fg-1)',
  },
  modalCloseButton: {
    background: 'none',
    border: 'none',
    fontSize: '1.5rem',
    color: 'var(--fg-2)',
    cursor: 'pointer',
    padding: '0.25rem',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '32px',
    height: '32px',
    borderRadius: '8px',
    transition: 'all 0.2s ease',
  },
  modalBody: {
    padding: 'var(--space-lg)',
    overflowY: 'auto' as const,
    flex: 1,
  },
  helpSection: {
    marginBottom: 'var(--space-lg)',
  },
  helpSectionTitle: {
    fontSize: '1rem',
    fontWeight: 600,
    color: 'var(--fg-1)',
    marginBottom: 'var(--space-md)',
    marginTop: 0,
  },
  helpList: {
    margin: 0,
    paddingLeft: '1.25rem',
    listStyleType: 'none' as const,
  },
  helpListItem: {
    marginBottom: 'var(--space-md)',
    fontSize: '0.875rem',
    lineHeight: 1.6,
    color: 'var(--fg-2)',
    position: 'relative' as const,
    paddingLeft: '0.75rem',
  },
};

export default App;
